import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Dither from './Dither';
import BlurText from './BlurText';

const DitherBackground = memo(() => (
  <div className="dither-container">
    <Dither
      waveColor={[0.9098039215686274, 1, 0.050980392156862744]}
      disableAnimation={true}
      enableMouseInteraction={false}
      mouseRadius={0.1}
      colorNum={16}
      pixelSize={1}
      waveAmplitude={0.35}
      waveFrequency={3.5}
      waveSpeed={0.01}
    />
  </div>
));
DitherBackground.displayName = 'DitherBackground';

const plans = [
  { id: 'static', name: 'Rotating Residential Basic', type: 'residential_basic' },
  { id: 'premium', name: 'Rotating Residential Premium', type: 'residential_premium' },
  { id: 'isp', name: 'Rotating ISP', type: 'isp' },
  { id: 'datacenter', name: 'Rotating Datacenter', type: 'datacenter' },
];

const planFeatures = {
  static: [
    'High quality residential IPs',
    'Rotating proxies',
    '99.9% uptime',
    'Great reliability',
    'SOCKS5 supported',
    'HTTP/HTTPS support',
    'Volume discounts',
    'API access'
  ],
  premium: [
    'Premium IP pool',
    'Highest quality providers',
    '99.99% uptime',
    'Fastest speeds',
    'SOCKS5 supported',
    'HTTP/HTTPS support',
    'Priority support',
    'API access'
  ],
  isp: [
    'ISP-grade IP addresses',
    'Rotating proxies',
    '99.9% uptime',
    'High speed & reliability',
    'SOCKS5 supported',
    'HTTP/HTTPS support',
    'Volume discounts',
    'API access'
  ],
  datacenter: [
    'Datacenter IP pool',
    'Fastest response times',
    '99.99% uptime',
    'Ultra-low latency',
    'SOCKS5 supported',
    'HTTP/HTTPS support',
    'Volume discounts',
    'API access'
  ],
};

const pricingTiers = {
  static: [
    { minGb: 1, maxGb: 4, pricePerGb: 0.80 },
    { minGb: 5, maxGb: 9, pricePerGb: 0.80 },
    { minGb: 10, maxGb: 49, pricePerGb: 0.80 },
    { minGb: 50, maxGb: 99, pricePerGb: 0.80 },
    { minGb: 100, maxGb: 249, pricePerGb: 0.80 },
    { minGb: 250, maxGb: 499, pricePerGb: 0.80 },
    { minGb: 500, maxGb: 999, pricePerGb: 0.80 },
    { minGb: 1000, maxGb: 2499, pricePerGb: 0.80 },
    { minGb: 2500, maxGb: 4999, pricePerGb: 0.59 },
    { minGb: 5000, maxGb: 9999, pricePerGb: 0.49 },
    { minGb: 10000, maxGb: null, pricePerGb: 0.40 },
  ],
  premium: [
    { minGb: 1, maxGb: 4, pricePerGb: 1.61 },
    { minGb: 5, maxGb: 9, pricePerGb: 1.61 },
    { minGb: 10, maxGb: 49, pricePerGb: 1.61 },
    { minGb: 50, maxGb: 99, pricePerGb: 1.61 },
    { minGb: 100, maxGb: 249, pricePerGb: 1.61 },
    { minGb: 250, maxGb: 499, pricePerGb: 1.61 },
    { minGb: 500, maxGb: 999, pricePerGb: 1.61 },
    { minGb: 1000, maxGb: 2499, pricePerGb: 1.59 },
    { minGb: 2500, maxGb: 4999, pricePerGb: 1.19 },
    { minGb: 5000, maxGb: 9999, pricePerGb: 0.98 },
    { minGb: 10000, maxGb: null, pricePerGb: 0.89 },
  ],
  isp: [
    { minGb: 1, maxGb: 24, pricePerGb: 1.30 },
    { minGb: 25, maxGb: 49, pricePerGb: 1.18 },
    { minGb: 50, maxGb: 99, pricePerGb: 1.06 },
    { minGb: 100, maxGb: 249, pricePerGb: 0.94 },
    { minGb: 250, maxGb: 499, pricePerGb: 0.82 },
    { minGb: 500, maxGb: 999, pricePerGb: 0.70 },
    { minGb: 1000, maxGb: 2499, pricePerGb: 0.58 },
    { minGb: 2500, maxGb: 4999, pricePerGb: 0.46 },
    { minGb: 5000, maxGb: null, pricePerGb: 0.40 },
  ],
  datacenter: [
    { minGb: 1, maxGb: 24, pricePerGb: 1.10 },
    { minGb: 25, maxGb: 49, pricePerGb: 0.99 },
    { minGb: 50, maxGb: 99, pricePerGb: 0.89 },
    { minGb: 100, maxGb: 249, pricePerGb: 0.79 },
    { minGb: 250, maxGb: 499, pricePerGb: 0.69 },
    { minGb: 500, maxGb: 999, pricePerGb: 0.59 },
    { minGb: 1000, maxGb: 2499, pricePerGb: 0.49 },
    { minGb: 2500, maxGb: 4999, pricePerGb: 0.39 },
    { minGb: 5000, maxGb: null, pricePerGb: 0.34 },
  ],
};

const PricingPackages = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activePlan, setActivePlan] = useState('static');
  const [sliderValue, setSliderValue] = useState(1);
  const [inputValue, setInputValue] = useState('1');
  const [sliderConfig, setSliderConfig] = useState({ min: 1, max: 12, step: 1 });

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    updatePlanConfig(activePlan);
  }, [activePlan]);

  const handleBuyNow = () => {
    if (user) {
      navigate('/dashboard/bandwidth');
    } else {
      navigate('/login', {
        state: {
          from: { pathname: '/dashboard/bandwidth' },
          selectedPlan: activePlan,
          selectedAmount: Math.round(sliderValue)
        }
      });
    }
  };

  const updatePlanConfig = () => {
    setSliderConfig({ min: 1, max: 10000, step: 1 });
    setSliderValue(1);
    setInputValue('1');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
      setSliderValue(Math.min(numValue, 10000));
    }
  };

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue);
    if (isNaN(numValue) || numValue < 1) {
      setSliderValue(1);
      setInputValue('1');
    } else {
      const clampedValue = Math.min(numValue, 10000);
      setSliderValue(clampedValue);
      setInputValue(String(clampedValue));
    }
  };

  const handleSliderChange = (e) => {
    const value = parseFloat(e.target.value);
    setSliderValue(value);
    setInputValue(String(Math.round(value)));
  };

  const getPricing = () => {
    const tiers = pricingTiers[activePlan] || [];
    const gbAmount = Math.round(sliderValue);

    const tier = tiers.find(t =>
      gbAmount >= t.minGb && (t.maxGb === null || gbAmount <= t.maxGb)
    );

    if (!tier) {
      const highestTier = tiers[tiers.length - 1];
      if (highestTier) {
        return {
          unitLabel: `Price Per GB:`,
          unitPrice: highestTier.pricePerGb.toFixed(4),
          total: (highestTier.pricePerGb * gbAmount).toFixed(2)
        };
      }
      return { unitLabel: 'Price:', unitPrice: '0.00', total: '0.00' };
    }

    return {
      unitLabel: `Price Per GB:`,
      unitPrice: tier.pricePerGb.toFixed(4),
      total: (tier.pricePerGb * gbAmount).toFixed(2)
    };
  };

  const pricing = getPricing();

  const getSliderBackground = () => {
    const { min, max } = sliderConfig;
    const percentage = ((sliderValue - min) / (max - min)) * 100;
    return `linear-gradient(to right, #E8FF00 0%, #E8FF00 ${percentage}%, rgba(255, 255, 255, 0.1) ${percentage}%, rgba(255, 255, 255, 0.1) 100%)`;
  };

  return (
    <section className="pricing-packages" id="pricing" ref={sectionRef}>
      <DitherBackground isVisible={isVisible} />

      <div className="container">
        <h2 className="pricing-title">
          <BlurText text="Pay as You Go" delay={62} direction="top" />
        </h2>
        <p className="pricing-subtitle">
          <BlurText text="Choose your suitable plan and start your journey" delay={62} direction="bottom" />
        </p>

        <div className="referral-promo">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          <span>Sign up with a referral code and get <strong>3% extra bandwidth</strong> on every purchase! Refer friends and earn <strong>2%</strong> of their purchases.</span>
        </div>

        <div className="pricing-content">
          <div className="pricing-plans">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`plan-item ${activePlan === plan.id ? 'active' : ''}`}
                onClick={() => setActivePlan(plan.id)}
              >
                <div className="plan-item-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="3" fill="currentColor"/>
                  </svg>
                </div>
                <span className="plan-item-name">{plan.name}</span>
                <div className="plan-item-arrow">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>

          <div className="pricing-details">
            <div className="pricing-slider">
              <div className="gb-selector">
                <label className="selector-label">Select GB Amount:</label>
                <div className="gb-input-wrapper">
                  <input
                    type="number"
                    min={sliderConfig.min}
                    max={sliderConfig.max}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className="gb-input"
                  />
                  <span className="gb-suffix">GB</span>
                </div>
              </div>
              <input
                type="range"
                min={sliderConfig.min}
                max={sliderConfig.max}
                step={sliderConfig.step}
                value={sliderValue}
                className="slider-input"
                onChange={handleSliderChange}
                style={{ background: getSliderBackground() }}
              />
            </div>

            <div className="pricing-info">
              <div className="pricing-info-row">
                <span className="pricing-label">Price:</span>
                <span className="pricing-value">${pricing.total}</span>
              </div>
              <div className="pricing-info-row">
                <span className="pricing-label">{pricing.unitLabel}</span>
                <span className="pricing-value">${pricing.unitPrice}</span>
              </div>
            </div>

            <div className="pricing-features">
              {planFeatures[activePlan].map((feature, index) => (
                <div className="pricing-feature" key={index}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 10L8 13L15 6" stroke="#E8FF00" strokeWidth="2"/>
                  </svg>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="pricing-summary">
              <div className="pricing-summary-row">
                <span>Unit Price:</span>
                <span className="pricing-summary-value">${pricing.unitPrice}</span>
              </div>
              <div className="pricing-summary-row total">
                <span>Total Price</span>
                <span className="pricing-summary-value">${pricing.total}</span>
              </div>
              <button className="pricing-buy-button" onClick={handleBuyNow}>Buy Now</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingPackages;
